import React, { useEffect, useState } from "react";
import axios from "axios";
import LandingPage from "./components/LandingPage";
import BotSimulator from "./components/BotSimulator";
import CrmDashboard from "./components/CrmDashboard";
import PedaHub from "./components/PedaHub";
import SocialIntegrations from "./components/SocialIntegrations";
import BotCustomizer from "./components/BotCustomizer";
import AdminGate from "./components/AdminGate";
import Header from "./components/Header";
import NoorVideoButton from "./components/NoorVideoButton";
import NoorLive from "./components/NoorLive";
import { COLLEGE_WHATSAPP_DISPLAY, COLLEGE_TEL, COLLEGE_TEL_DISPLAY, COLLEGE_FAX_DISPLAY, COLLEGE_EMAIL, COMPANY_CP, buildWhatsAppLink } from "./constants/college";
import { useT } from "./i18n";
import "./App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function App() {
    const [lang, setLang] = useState(() => localStorage.getItem("lang") || "ar");
    const [activeTab, setActiveTab] = useState("landing"); // landing | simulator | crm | peda | integrations
    const [adminToken, setAdminToken] = useState(() => localStorage.getItem("admin_token") || "");
    const [courses, setCourses] = useState([]);
    const [quizCategories, setQuizCategories] = useState([]);
    const [liveOpen, setLiveOpen] = useState(false);

    const T = useT(lang);
    const dir = lang === "he" || lang === "ar" ? "rtl" : "ltr";

    useEffect(() => {
        document.documentElement.dir = dir;
        document.documentElement.lang = lang;
        localStorage.setItem("lang", lang);
    }, [dir, lang]);

    // Ad traffic bridge: capture UTM params and auto-open the bot when arriving
    // from a Facebook/Instagram ad. This is used while Meta App Review is pending
    // so all paid traffic funnels into the on-site chatbot.
    useEffect(() => {
        try {
            const params = new URLSearchParams(window.location.search);
            const path = window.location.pathname || "";
            const utm = {
                source: params.get("utm_source") || "",
                medium: params.get("utm_medium") || "",
                campaign: params.get("utm_campaign") || "",
                content: params.get("utm_content") || "",
                term: params.get("utm_term") || "",
            };
            const hasUtm = Object.values(utm).some(Boolean);
            if (hasUtm) {
                sessionStorage.setItem("aa_utm", JSON.stringify(utm));
                const src = (utm.source || "").toLowerCase();
                let bucket = "direct";
                if (src.includes("facebook") || src.includes("fb")) bucket = "facebook_ad";
                else if (src.includes("instagram") || src.includes("ig")) bucket = "instagram_ad";
                else if (src) bucket = src;
                sessionStorage.setItem("aa_source", bucket);
            }
            // Direct routes: /admin → CRM, /bot → chatbot, ?tab=crm → CRM
            const tabParam = params.get("tab");
            if (path.startsWith("/admin") || tabParam === "crm") {
                setActiveTab("crm");
            } else if (path.startsWith("/bot") || tabParam === "simulator" || params.get("open_bot") === "1" || params.get("chat") === "1") {
                setActiveTab("simulator");
            } else if (tabParam === "peda" || path.startsWith("/courses")) {
                setActiveTab("peda");
            } else if (tabParam === "settings" || path.startsWith("/settings")) {
                setActiveTab("settings");
            } else if (tabParam === "integrations" || path.startsWith("/integrations")) {
                setActiveTab("integrations");
            }
        } catch (e) {
            // Silent — non-critical enhancement
        }
    }, []);

    useEffect(() => {
        axios.get(`${API}/courses`)
            .then((r) => setCourses(r.data))
            .catch((e) => console.error("Failed to load courses", e));
        axios.get(`${API}/quiz/categories`)
            .then((r) => setQuizCategories(r.data))
            .catch((e) => console.error("Failed to load quiz categories", e));
    }, []);

    const handleLogin = (token) => {
        localStorage.setItem("admin_token", token);
        setAdminToken(token);
    };

    const handleLogout = () => {
        localStorage.removeItem("admin_token");
        setAdminToken("");
        setActiveTab("landing");
    };

    const isAdminTab = activeTab === "crm" || activeTab === "integrations" || activeTab === "settings";
    const requireAdmin = isAdminTab && !adminToken;

    return (
        <div className="App min-h-screen bg-white" dir={dir}>
            <Header
                lang={lang}
                setLang={setLang}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                isAdmin={Boolean(adminToken)}
                onLogout={handleLogout}
            />

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                {requireAdmin ? (
                    <AdminGate API={API} lang={lang} onLogin={handleLogin} />
                ) : (
                    <>
                        {activeTab === "landing" && (
                            <LandingPage
                                lang={lang}
                                courses={courses}
                                onStartBot={() => setActiveTab("simulator")}
                                onSeeCourses={() => setActiveTab("peda")}
                            />
                        )}
                        {activeTab === "simulator" && (
                            <BotSimulator lang={lang} API={API} />
                        )}
                        {activeTab === "crm" && (
                            <CrmDashboard lang={lang} API={API} token={adminToken} courses={courses} onUnauthorized={handleLogout} />
                        )}
                        {activeTab === "peda" && (
                            <PedaHub lang={lang} API={API} categories={quizCategories} />
                        )}
                        {activeTab === "integrations" && (
                            <SocialIntegrations lang={lang} />
                        )}
                        {activeTab === "settings" && (
                            <BotCustomizer API={API} token={adminToken} onUnauthorized={handleLogout} />
                        )}
                    </>
                )}
            </main>

            <footer className="border-t border-[#E2E8F0] mt-16 py-8 text-center text-sm text-[#475569]">
                <div className="max-w-3xl mx-auto px-4 space-y-3">
                    <div className="flex flex-wrap items-center justify-center gap-4 text-[13px]">
                        <a
                            href={buildWhatsAppLink(lang === "ar" ? "مرحباً!" : "שלום!")}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1.5 font-bold text-[#128C7E] hover:text-[#25D366] transition-colors"
                            data-testid="footer-whatsapp-link"
                        >
                            <svg viewBox="0 0 32 32" width="16" height="16" fill="currentColor" aria-hidden="true">
                                <path d="M16 3C9.373 3 4 8.373 4 15c0 2.35.68 4.542 1.849 6.393L4 29l7.786-1.795A11.94 11.94 0 0 0 16 27c6.627 0 12-5.373 12-12S22.627 3 16 3z"/>
                            </svg>
                            <span dir="ltr">{COLLEGE_WHATSAPP_DISPLAY}</span>
                            <span className="text-[10px] opacity-70">WhatsApp</span>
                        </a>
                        <a
                            href={`tel:${COLLEGE_TEL}`}
                            className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#D4761F] transition-colors"
                            data-testid="footer-tel-link"
                        >
                            📞 <span dir="ltr">{COLLEGE_TEL_DISPLAY}</span>
                            <span className="text-[10px] opacity-70">{lang === "ar" ? "المكتب" : "משרד"}</span>
                        </a>
                        <span className="inline-flex items-center gap-1.5 text-[#475569]" data-testid="footer-fax">
                            📠 <span dir="ltr">{COLLEGE_FAX_DISPLAY}</span>
                            <span className="text-[10px] opacity-70">{lang === "ar" ? "فاكس" : "פקס"}</span>
                        </span>
                        <a
                            href={`mailto:${COLLEGE_EMAIL}`}
                            className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#D4761F] transition-colors"
                            data-testid="footer-email-link"
                        >
                            ✉️ <span dir="ltr">{COLLEGE_EMAIL}</span>
                        </a>
                    </div>
                    <div>{T.footer}</div>
                    <div className="pt-2 border-t border-[#E2E8F0]/60 text-[12px] text-[#64748B]" data-testid="legal-entity-footer" dir="rtl">
                        מופעל על ידי: <span className="font-semibold text-[#1A365D]">מכללת באר שבע הקהילתית בע״מ</span>
                        <span className="mx-1.5 text-[#CBD5E1]">·</span>
                        <span className="font-mono text-[11px]">ח.פ. {COMPANY_CP}</span>
                        <span className="mx-1.5 text-[#CBD5E1]">·</span>
                        <a href="/privacy.html" className="hover:text-[#D4761F] underline underline-offset-2" target="_blank" rel="noreferrer">מדיניות פרטיות</a>
                        <span className="mx-1.5 text-[#CBD5E1]">·</span>
                        <a href="/terms.html" className="hover:text-[#D4761F] underline underline-offset-2" target="_blank" rel="noreferrer">תנאי שימוש</a>
                    </div>
                </div>
            </footer>

            {/* Floating WhatsApp contact — disabled per user request */}

            {/* Floating Noor video button — visible on every non-admin page */}
            {!requireAdmin && activeTab !== "simulator" && (
                <NoorVideoButton
                    lang={lang}
                    onOpenBot={() => setActiveTab("simulator")}
                    onOpenLive={() => setLiveOpen(true)}
                />
            )}

            {/* Noor Live — Phase 3 demo (full-screen conversational avatar) */}
            <NoorLive
                lang={lang}
                API={API}
                open={liveOpen}
                onClose={() => setLiveOpen(false)}
                onOpenChat={() => { setLiveOpen(false); setActiveTab("simulator"); }}
            />
        </div>
    );
}
